<template>
  <v-dialog
    v-model="isDialogVisible"
    width="50%"
    @click:outside="onHandleDialogAction"
    @keydown.esc="onHandleDialogAction"
  >
    <v-card>
      <v-toolbar
        color="blue-grey-darken-2"
      >
        <v-card-title>
          {{ $t('ErrorItemDialog.error_headline') }}
        </v-card-title>
      </v-toolbar>
      <div v-if="hasSuccessfulItems" class="px-4 py-2 mt-2">
        {{ successfulElements }}
      </div>
      <div v-if="hasFailedItems" class="px-4 py-2">
        {{ failedElements }}
      </div>
      <v-textarea
        class="px-4 py-2"
        disabled
        :model-value="generateTranslatedErrorString()"
      />
      <v-card-actions>
        <v-row
          align="center"
          justify="center"
          no-gutters
        >
          <v-btn
            color="primary"
            variant="text"
            @click="generateTextFile"
          >
            {{ $t('ErrorItemDialog.button_download') }}
          </v-btn>
          <v-btn
            color="primary"
            variant="text"
            @click="onHandleDialogAction"
          >
            {{ $t('ErrorItemDialog.button_confirm') }}
          </v-btn>
        </v-row>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import {
  computed,
  onMounted,
  onUnmounted,
  ref,
} from 'vue';
import type { PropType } from 'vue';

import {
  useI18n
} from '@/composables/useI18n';
import type {ErrorItemResult, OperationResults} from "@/scripts/Common";
import {FileDownload} from "@/scripts/Common/FileDownload";
import {TXT_EXTENSION} from "@/scripts/Constants";


const props = defineProps({
  response: {
    required: false,
    type: Object as PropType<OperationResults<any>>,
  },
});

const emits = defineEmits([
  'onDialogCancelOrConfirm',
]);

const {
  t,
  te
} = useI18n();

const isDialogVisible = ref<boolean>(false);

const hasSuccessfulItems = computed(() => (props.response?.successfulItems.length ?? 0) > 0)
const hasFailedItems = computed(() => (props.response?.failedItems.length ?? 0) > 0)

const successfulElements = computed(() => {
  const elements = props.response?.successfulItems.join(', ');
  return `${t('ErrorItemDialog.successful_items')}: ${elements}`
})

const failedElements = computed(() => {
  const elements = props.response?.failedItems.map((element: any) => element.id).join(', ') ?? '';
  return `${t('ErrorItemDialog.failed_items')}: ${elements}`
})

const formatErrorItemResult = (item: ErrorItemResult): string[] => item.errorCodes
  .map(element => te(`errorCodes.${element}`) ? t(`ErrorItemDialog.errorCodes.${element}`) : element);

const translatedErrorMessages = computed(() => {
  return props.response?.failedItems.map((element: ErrorItemResult) => ({ item: element.id, errors: formatErrorItemResult(element)}))
})

const generateTranslatedErrorString = () => {
  try{
    //replace bracked, quotes commas and empty lines.
    return JSON.stringify(translatedErrorMessages.value, undefined, 2)
      .replace(/[\[\]{}",']+/g, '')
      .replace(/^\s*\n/gm, '');
  }
  catch (e) {
    return `${t('ErrorItemDialog.server_error')}, ${t('ErrorItemDialog.try_again_later')}`;
  }
}

const generateTextFile = () => {
  const textFile = new Blob([generateTranslatedErrorString()], { type: "text/plain;charset=utf-8" });

  FileDownload.Create(textFile, `${t('ErrorItemDialog.error_filename')}.${TXT_EXTENSION}`);
}

const onHandleDialogAction = () => {
  isDialogVisible.value = false;
  emits('onDialogCancelOrConfirm');
}

onMounted(() => {
  isDialogVisible.value = true;
})

onUnmounted(() => {
  isDialogVisible.value = false;
})
</script>

